import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageTransition from "../WrapComponents/PageTransition.jsx";
import RevealOnScroll from "../WrapComponents/RevealOnScroll.jsx";
import axiosInstance from "../Axiosintances/Axiosinstance.jsx";

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axiosInstance.get(
          "/service/api/v1/public_services",
        );
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return (
    <PageTransition>
      <div className="flex flex-col bg-white dark:bg-gray-950 transition-colors duration-300">
        {/* Hero Section */}
        <section className="relative w-full h-[85vh] min-h-[600px] bg-gray-900 overflow-hidden">
          <div className="absolute inset-0 img-zoom-container">
            <img
              src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/project-lock/bannerimg.png"
              alt="Waterproofing Project"
              className="absolute inset-0 w-full h-full object-cover opacity-60 img-zoom-target scale-105 duration-[20s]"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70"></div>

          <div className="absolute inset-0 flex items-center justify-center text-center px-4">
            <RevealOnScroll className="max-w-5xl mx-auto" duration={1000}>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-8 tracking-tight leading-tight drop-shadow-lg">
                Building the Future,
                <br /> Securing the Present.
              </h1>
              <p className="text-lg md:text-2xl text-gray-100 mb-12 max-w-2xl mx-auto font-light leading-relaxed drop-shadow-md">
                TriShield protects your building through integrated
                waterproofing, structural, power, and security solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link
                  to="/services"
                  className="group bg-white text-black px-10 py-4 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-xl flex items-center justify-center gap-2"
                >
                  Explore Services{" "}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full font-bold hover:bg-white/10 transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center backdrop-blur-sm"
                >
                  Get a Quote
                </Link>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-14  md:py-24 bg-white dark:bg-gray-950 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealOnScroll className="max-w-7xl mb-20">
              <span className="text-sm font-bold tracking-widest text-gray-400 dark:text-gray-500 uppercase mb-3 block">
                Who We Are
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
                Mission & Values
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg md:text-xl font-light">
                TriShield represents our commitment to protecting what matters
                most—your building’s safety, reliability, and longevity. Built
                on our core values of excellence, integrity, and innovation,
                TriShield unifies AQUA waterproofing, ARMOR structural
                protection, POWER resilience, and ACCESS automation into one
                purposeful system. Our mission is not just to repair problems
                but to uncover the root cause, deliver long-term solutions, and
                ensure every structure we safeguard remains strong, efficient,
                and secure for years to come.
              </p>
            </RevealOnScroll>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {loading ? (
                // Loading Skeletons
                [1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white dark:bg-gray-900 p-8 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm animate-pulse h-full text-center flex flex-col items-center"
                  >
                    <div className="w-32 h-32 mb-8 rounded-full bg-gray-200 dark:bg-gray-800"></div>
                    <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-2/3"></div>
                  </div>
                ))
              ) : services.length > 0 ? (
                services.slice(0, 3).map((service, index) => (
                  <RevealOnScroll
                    key={service.id || index}
                    delay={(index + 1) * 100}
                    className="group h-full"
                  >
                    <Link to="/services" className="block h-full">
                      <div className="bg-white dark:bg-gray-900 p-8 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-2xl dark:hover:shadow-black/50 transition-all duration-500 hover:-translate-y-2 h-full text-center flex flex-col items-center">
                        <div className="w-32 h-32 mb-8 rounded-full overflow-hidden shadow-lg img-zoom-container bg-gray-100 dark:bg-gray-800">
                          <img
                            src={service.image}
                            alt={service.name}
                            className="w-full h-full object-cover img-zoom-target"
                          />
                        </div>
                        <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                          {service.name}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
                          {service.description}
                        </p>
                      </div>
                    </Link>
                  </RevealOnScroll>
                ))
              ) : (
                <div className="col-span-full text-center py-10 text-gray-500">
                  No services found.
                </div>
              )}
            </div>
          </div>
        </section>
        {/* Why Project Lock Section */}
        <section className="py-10 md:py-16 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Heading */}
            <RevealOnScroll className="text-center mb-8">
              <span className="text-sm font-bold tracking-widest text-gray-400 dark:text-gray-500 uppercase block mb-3">
                Our Process
              </span>

              <div className="flex justify-center my-2 ">
                <div>
                  {/* Mobile Image */}
                  <img
                    src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/project-lock/hf_20260228_113856_7587fbc1-54de-40c2-94e7-7fb2aa41e919%20(1).jpeg"
                    alt="Mobile Project Lock"
                    className="block md:hidden border rounded-xl"
                  />

                  {/* Desktop/Web Image */}
                  <img
                    src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/project-lock/hf_20260228_113836_28fbc29a-603b-40e3-bf8f-eeb98adc2b13%20(1).jpeg"
                    alt="Desktop Project Lock"
                    className="hidden md:block border rounded-xl h-[550px]"
                  />
                </div>
              </div>
            </RevealOnScroll>
            <RevealOnScroll>
              <h2 className="text-3xl md:text-5xl font-bold md:mb-10 mb-6 text-gray-900 dark:text-white text-center">
                Why Choose Project Lock?
              </h2>
              {/* Features */}
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300">
                <p className="text-lg text-gray-700 dark:text-gray-300 font-medium md:[text-align:justify]">
                  We take an engineering-first approach to diagnosis, using only
                  premium components and backing every project with our trusted
                  TriShield Warranty. Our solutions are crafted for discerning
                  clients who demand precision, reliability, and unmatched
                  excellence. Every recommendation is driven by data, technical
                  insight, and years of field expertise, ensuring you receive
                  solutions that are not just effective but built to last. With
                  a commitment to clean, organized, and professional execution,
                  we ensure that every stage of the process reflects our
                  elevated standards. From the initial inspection to the final
                  handover, our workflow is streamlined, transparent, and
                  engineered for perfection. In everything we do, we stay
                  focused on what matters most: Inspect. Diagnose. Solve.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="py-10  md:py-16 bg-white dark:bg-gray-950 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealOnScroll className="text-center mb-16">
              <span className="text-sm font-bold tracking-widest text-gray-400 dark:text-gray-500 uppercase block mb-3">
                Industries We Serve
              </span>

              <div className="flex justify-center my-2 ">
                {/* Mobile Image */}
                <img
                  src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/uploads/Screenshot%202026-02-27%20230439.png"
                  alt="Mobile Project Lock"
                  className="block md:hidden border rounded-xl"
                />

                {/* Desktop/Web Image */}
                <img
                  src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/project-lock/hf_20260228_124632_5b4ae698-0c0a-4a59-a01a-7368b974ce8d.jpeg"
                  alt="Desktop Project Lock"
                  className="hidden md:block border rounded-xl h-[550px]"
                />
              </div>
            </RevealOnScroll>

            <RevealOnScroll>
              <h2 className="text-3xl md:text-5xl font-bold md:mb-10 mb-6 text-gray-900 dark:text-white text-center">
                Who We Work With
              </h2>
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300">
                <p className="text-lg text-gray-700 dark:text-gray-300 font-medium md:[text-align:justify]">
                  We proudly serve a wide spectrum of industries, working
                  closely with resorts, architects, premium villas, apartments,
                  and builders to deliver projects that embody luxury,
                  innovation, and precision. From creating immersive
                  environments for high-end resorts to collaborating with
                  architects on cutting-edge designs, our expertise extends
                  across both commercial and residential spaces. We support
                  premium villa and apartment developments with refined
                  detailing, durable materials, and a deep commitment to
                  aesthetic and structural excellence. Our strong partnerships
                  with builders allow us to bring visionary concepts to life
                  seamlessly. With years of experience in high-end construction,
                  we understand the level of craftsmanship, customization, and
                  quality that discerning clients expect, ensuring every project
                  stands out for its sophistication and long-lasting value.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        {/* Our Process */}
        <section className="py-10 md:py-16 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* --- Top Title + Image Section (Perfect Structural Match) --- */}
            <RevealOnScroll className="text-center md:mb-52">
              <span className="text-sm font-bold tracking-widest text-gray-400 dark:text-gray-500 uppercase block mb-3">
                Our framework
              </span>

              <div className="flex justify-center my-2 md:h-96">
                <div>

                  {/* Mobile Image */}
                  <img
                    src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/project-lock/hf_20260228_114754_0c4193b6-9c66-43cd-9022-b272aee22df4.jpeg"
                    alt="Mobile Project Lock"
                    className="block md:hidden border rounded-xl"
                  />

                  {/* Desktop Image */}
                  <img
                    src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/project-lock/hf_20260228_114203_1eb45bcc-aea3-4efe-aae6-1c50a36400ef%20(1).jpeg"
                    alt="Desktop Project Lock"
                    className="hidden md:block border rounded-xl h-[550px]"
                  />

                </div>
              </div>
            </RevealOnScroll>

            {/* --- Heading + Text Box Section (Matches Your Other Box) --- */}
            <RevealOnScroll className="md:mt-10">
              <h2 className="text-3xl md:text-5xl font-bold md:mb-10 mb-6 text-gray-900 dark:text-white text-center ">
                How We Work
              </h2>

              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300">
                <p className="text-lg text-gray-700 dark:text-gray-300 font-medium md:[text-align:justify]">
                  Our work process is designed to be simple, systematic, and reliable,
                  ensuring a smooth experience from start to finish. We begin with a
                  thorough site inspection to understand the location, requirements,
                  and project scope. Based on this assessment, our team develops a
                  precise engineering plan that outlines the technical details and
                  execution strategy.

                  Once approved, our skilled team carries out professional installation
                  using high-quality materials and best-in-class practices. After installation,
                  we conduct a rigorous quality check to ensure every element meets our standards
                  of safety, durability, and performance.

                  Finally, we provide a transparent warranty handover, giving you peace of mind
                  and long-term confidence in the work we deliver.
                </p>
              </div>
            </RevealOnScroll>

          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default HomePage;
